import React from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';

import { LinesEllipsis } from './LinesEllipsis';
import {
  getColorDisplayStatus,
  ORIGIN_AUTONOMOUS,
  ORIGIN_AUTONOMOUS_LOCAL_GROUP,
  ORIGIN_AUTONOMOUS_THEME_CIRCLE,
} from '../utils';

const InitiativeCardContainer = styled.div<{ secondaryBg: boolean }>`
  width: 350px;
  height: 370px;
  background-color: ${({ secondaryBg }) => (secondaryBg ? '#f1fbfe' : '#fff')};
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 10%), 0 2px 4px -1px rgb(0 0 0 / 6%);
  border-radius: 0.375rem;
  font-family: Arial;
`;

const InitiativeCardHeader = styled.div`
  box-sizing: border-box;
  height: 150px;
  padding: 1rem 2rem;
  border-bottom: 1px solid #e0e0e0;
`;

const InitiativeCardTitle = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  font-size: 1.25rem;
  font-weight: bold;
`;

const InitiativeInfoList = styled.div`
  display: flex;
  height: 35px;
`;

const InitiativeInfoItem = styled.div<{ secondaryBg?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  :first-of-type {
    margin: 0 1rem 0 0;
  }
  color: ${({ secondaryBg }) => (secondaryBg ? '#02788C' : null)};
`;

const InitiativeCardBody = styled.div`
  box-sizing: border-box;
  height: 220px;
  font-size: 0.875rem;
  padding: 1rem 2rem;
`;

const InitiativeShortDesc = styled.div`
  height: 100px;
  line-height: 20px;
  text-align: justify;
`;

const InitiativeThematicsTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
`;

const InitiativeTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  box-sizing: border-box;

  align-items: center;
  height: 40px;
  gap: 5px;
  margin: 0.5rem 0;
`;

const InitiativeInfoItemTxt = styled.div`
  opacity: 0.8;
`;

const InitiativeStatus = styled.span<{ statusColor: string }>`
  background-color: ${({ statusColor }) =>
    statusColor ? statusColor : '#424242'};

  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.5rem;
  color: white;
  text-transform: uppercase;
`;

const InitiativeCard = ({
  title,
  creationDate,
  status,
  origin,
  pilote,
  shortDesc,
  thematiques,
}) => {
  const date = moment(creationDate);
  const autonomousInitiativeTypes = [
    ORIGIN_AUTONOMOUS,
    ORIGIN_AUTONOMOUS_LOCAL_GROUP,
    ORIGIN_AUTONOMOUS_THEME_CIRCLE,
  ];
  const isAutonomousInitiative = autonomousInitiativeTypes.includes(origin);

  /**
   * We only allows 4 thematics to avoid ugly display
   */
  const maxThematicsToAvoidBreakUI = 4;
  let thematicsToDisplay = thematiques
    .sort((a, b) => a.length - b.length)
    .filter((_, idx) => idx <= maxThematicsToAvoidBreakUI - 1)
    .map((t, idx) => {
      return (
        <Chip
          key={idx}
          color="primary"
          label={t}
          variant="outlined"
          size="small"
        />
      );
    });
  if (thematiques.length > maxThematicsToAvoidBreakUI) {
    const thematicsLeft = thematiques.filter(
      (_, idx) => idx > maxThematicsToAvoidBreakUI - 1,
    );
    thematicsToDisplay = [
      ...thematicsToDisplay,
      <Tooltip key="..." title={thematicsLeft.join(', ')} placement="top">
        <Chip color="primary" label="..." variant="outlined" size="small" />
      </Tooltip>,
    ];
  }
  return (
    <InitiativeCardContainer secondaryBg={isAutonomousInitiative}>
      <InitiativeCardHeader>
        <InitiativeCardTitle title={title}>
          <LinesEllipsis maxLine={2}>{title}</LinesEllipsis>
        </InitiativeCardTitle>
        <InitiativeInfoList>
          <InitiativeInfoItem style={{ gap: '5px' }}>
            <CalendarMonthIcon fontSize="small" color="primary" />
            <InitiativeInfoItemTxt>{`Créé le ${date.format(
              'DD/MM/YYYY',
            )}`}</InitiativeInfoItemTxt>
          </InitiativeInfoItem>
          <InitiativeInfoItem>
            <InitiativeStatus statusColor={getColorDisplayStatus(status)}>
              {status}
            </InitiativeStatus>
          </InitiativeInfoItem>
        </InitiativeInfoList>
        <InitiativeInfoList>
          <InitiativeInfoItem
            secondaryBg={isAutonomousInitiative}
            style={{ gap: '5px' }}
          >
            <PersonIcon fontSize="small" color="primary" />
            <InitiativeInfoItemTxt>
              {origin}{' '}
              {pilote && pilote !== ' ' ? `pilotée par ${pilote}` : null}
            </InitiativeInfoItemTxt>
          </InitiativeInfoItem>
        </InitiativeInfoList>
      </InitiativeCardHeader>
      <InitiativeCardBody>
        <InitiativeShortDesc>
          <LinesEllipsis maxLine={5}>{shortDesc ?? ''}</LinesEllipsis>
        </InitiativeShortDesc>
        <InitiativeTagsContainer>
          <InitiativeThematicsTitle>Thématiques :</InitiativeThematicsTitle>
          {thematicsToDisplay}
        </InitiativeTagsContainer>
      </InitiativeCardBody>
    </InitiativeCardContainer>
  );
};

export { InitiativeCard };
